import { z } from "zod";
import { secondsToMs, sleep } from "./time";

const LOCK_PREFIX = "";

const LockValidator = z.object({
    issuedAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    expiration: z.coerce.date().default(() => {
        const expiration = new Date();
        expiration.setTime(expiration.getTime() + secondsToMs(5));
        return expiration;
    }),
});

export function semaphore<T>(name: string, cb: () => T): Promise<Promise<T>> {
    return navigator.locks.request(name, { mode: "exclusive" }, () => localStorageLock(name, cb));
}

async function localStorageLock<T>(name: string, cb: () => T): Promise<T> {
    const lockName = `${LOCK_PREFIX}_${name}`;

    while (true) {
        const item = localStorage.getItem(lockName);

        if (!item) {
            break;
        }

        const lockObject = LockValidator.parse(JSON.parse(item));

        if (lockObject.expiration.getTime() < Date.now()) {
            break;
        }

        await sleep(30);
    }

    const start = new Date();
    const setFunction = () => {
        const lockObject: z.input<typeof LockValidator> = {
            issuedAt: start,
            updatedAt: new Date(),
        };

        localStorage.setItem(lockName, JSON.stringify(LockValidator.parse(lockObject)));
    };

    setFunction();

    const intervalId = setInterval(setFunction, 300);

    try {
        return cb();
    } finally {
        clearInterval(intervalId);
        localStorage.removeItem(lockName);
    }
}
